/**
 * RelationshipPanel — CRM-owned relationship facts on the customer profile.
 *
 *   - is_global flag + fee_structure (free text) + has_partner_agency flag
 *     → saved via PUT /api/customer-settings/[id] (audited).
 *   - Entity areas (countries/regions with a legal presence)
 *     → CRUD /api/customers/[id]/entities
 *   - Partner agencies (other TMCs the customer splits travel with)
 *     → CRUD /api/customers/[id]/partner-agencies
 *
 * These never come from Domo — this panel is the only place they're set.
 * Edits require edit:customer_settings; viewers see a read-only rendering.
 */

import { useEffect, useState } from 'react';
import { apiFetch, ApiError } from '@/features/crm/lib/api-fetch';
import { useMe } from '@/features/crm/auth/use-capabilities';

interface Settings {
  isGlobal: boolean;
  feeStructure: string | null;
  hasPartnerAgency: boolean;
  intlTravelPct: number | null;
  npsScore: number | null;
  npsResponseCount: number | null;
  hasRecord: boolean;
}
interface Entity {
  id: string;
  country: string;
  label: string | null;
  notes: string | null;
}
interface Agency {
  id: string;
  agencyName: string;
  notes: string | null;
}

export function RelationshipPanel({ customerId }: { customerId: string }) {
  const { can } = useMe();
  const canEdit = can('edit:customer_settings');
  // Unlinking an entity or a partner agency removes a record; adding one does
  // not. Deletion stays with admin+manager (2026-08-11) even though every
  // internal role can now edit, so the two controls take different capabilities.
  const canDelete = can('delete:customer_record');

  const [settings, setSettings] = useState<Settings | null>(null);
  const [entities, setEntities] = useState<Entity[]>([]);
  const [agencies, setAgencies] = useState<Agency[]>([]);
  const [error, setError] = useState<string | null>(null);

  // local edit state for the scalar fields
  const [isGlobal, setIsGlobal] = useState(false);
  const [feeStructure, setFeeStructure] = useState('');
  const [hasPartnerAgency, setHasPartnerAgency] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  const enc = encodeURIComponent(customerId);

  const load = async () => {
    try {
      const [s, e, a] = await Promise.all([
        apiFetch<Settings>(`/api/customer-settings/${enc}`),
        apiFetch<{ entities: Entity[] }>(`/api/customers/${enc}/entities`),
        apiFetch<{ agencies: Agency[] }>(
          `/api/customers/${enc}/partner-agencies`,
        ),
      ]);
      setSettings(s);
      setIsGlobal(s.isGlobal);
      setFeeStructure(s.feeStructure ?? '');
      setHasPartnerAgency(s.hasPartnerAgency);
      setEntities(e.entities);
      setAgencies(a.agencies);
      setDirty(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  const saveScalars = async () => {
    try {
      setSaving(true);
      setError(null);
      await apiFetch(`/api/customer-settings/${enc}`, {
        method: 'PUT',
        body: JSON.stringify({
          isGlobal,
          feeStructure: feeStructure.trim() || null,
          hasPartnerAgency,
        }),
      });
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  const addEntity = async (country: string, label: string) => {
    await apiFetch(`/api/customers/${enc}/entities`, {
      method: 'POST',
      body: JSON.stringify({ country, label: label || null }),
    });
    await load();
  };
  const delEntity = async (id: string) => {
    await apiFetch(`/api/customers/${enc}/entities?row_id=${id}`, {
      method: 'DELETE',
    });
    await load();
  };
  const addAgency = async (name: string) => {
    await apiFetch(`/api/customers/${enc}/partner-agencies`, {
      method: 'POST',
      body: JSON.stringify({ agency_name: name }),
    });
    await load();
  };
  const delAgency = async (id: string) => {
    await apiFetch(`/api/customers/${enc}/partner-agencies?row_id=${id}`, {
      method: 'DELETE',
    });
    await load();
  };

  const mark =
    <T,>(setter: (v: T) => void) =>
    (v: T) => {
      setter(v);
      setDirty(true);
    };

  return (
    <div className="section">
      <div className="section-header">
        <span className="section-label">Relationship</span>
        {settings && !settings.hasRecord && (
          <span className="section-meta">Not yet set</span>
        )}
      </div>
      <div className="section-body">
        {error && (
          <div className="banner banner-error" style={{ marginBottom: 10 }}>
            {error}
          </div>
        )}

        {/* Auto-global signal: meaningful international travel but not flagged
            Global. Threshold = 5% of trailing-12-month segments international. */}
        {settings &&
          !isGlobal &&
          settings.intlTravelPct != null &&
          settings.intlTravelPct > 5 && (
            <div
              className="banner"
              style={{
                marginBottom: 12,
                background: 'rgba(14,125,154,0.10)',
                border: '1px solid rgba(14,125,154,0.35)',
                color: '#0E7D9A',
                fontSize: 'var(--fs-base)',
              }}
            >
              {settings.intlTravelPct.toFixed(1)}% of recent travel is
              international — consider marking this account Global.
            </div>
          )}

        {/* NPS (read-only, trailing 12 months from survey data) */}
        {settings && settings.npsScore != null && (
          <div style={{ marginBottom: 14 }}>
            <FieldLabel>Net Promoter Score · 12mo</FieldLabel>
            <div
              style={{
                display: 'flex',
                alignItems: 'baseline',
                gap: 8,
                marginTop: 4,
              }}
            >
              <span
                style={{
                  fontSize: 'var(--fs-lg)',
                  fontWeight: 700,
                  color:
                    settings.npsScore >= 50
                      ? 'var(--green-strong)'
                      : settings.npsScore >= 0
                        ? 'var(--text-primary)'
                        : 'var(--red-strong)',
                }}
              >
                {settings.npsScore > 0
                  ? `+${settings.npsScore}`
                  : settings.npsScore}
              </span>
              <span
                style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-muted)' }}
              >
                from {settings.npsResponseCount ?? 0} response
                {settings.npsResponseCount === 1 ? '' : 's'}
              </span>
            </div>
          </div>
        )}

        {/* Scalar relationship facts */}
        <div className="form-grid" style={{ gap: 12, marginBottom: 14 }}>
          <CheckField
            label="Global account"
            sub="Has travel / entities outside the US"
            value={isGlobal}
            disabled={!canEdit}
            onChange={mark(setIsGlobal)}
          />
          <CheckField
            label="Splits travel with a partner agency"
            sub="Another TMC handles part of the program"
            value={hasPartnerAgency}
            disabled={!canEdit}
            onChange={mark(setHasPartnerAgency)}
          />
        </div>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <FieldLabel>Fee structure</FieldLabel>
          <textarea
            className="input"
            rows={2}
            placeholder="e.g. $18 per online txn, $32 full-service; 3% mgmt fee on hotel"
            value={feeStructure}
            disabled={!canEdit}
            onChange={(e) => mark(setFeeStructure)(e.target.value)}
          />
        </label>

        {canEdit && dirty && (
          <div
            style={{
              display: 'flex',
              gap: 8,
              justifyContent: 'flex-end',
              marginTop: 10,
            }}
          >
            <button
              className="btn btn-outline btn-sm"
              disabled={saving}
              onClick={() => load()}
            >
              Reset
            </button>
            <button
              className="btn btn-primary btn-sm"
              disabled={saving}
              onClick={saveScalars}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        )}

        {/* Entities */}
        <SubList
          title="Entity areas"
          empty="No entities recorded."
          items={entities.map((e) => ({
            id: e.id,
            primary: e.country,
            secondary: e.label,
          }))}
          canEdit={canEdit}
          canDelete={canDelete}
          addPlaceholder="Country / region"
          addSecondaryPlaceholder="Office / entity name (optional)"
          onAdd={addEntity}
          onDelete={delEntity}
        />

        {/* Partner agencies */}
        <SubList
          title="Partner agencies"
          empty="No partner agencies recorded."
          items={agencies.map((a) => ({
            id: a.id,
            primary: a.agencyName,
            secondary: a.notes,
          }))}
          canEdit={canEdit}
          canDelete={canDelete}
          addPlaceholder="Agency name (BCD / TTI / Eton / …)"
          onAdd={(name) => addAgency(name)}
          onDelete={delAgency}
        />
      </div>
    </div>
  );
}

// ─── reusable sub-list (entities / agencies) ──────────────────────────────

function SubList({
  title,
  empty,
  items,
  canEdit,
  canDelete,
  addPlaceholder,
  addSecondaryPlaceholder,
  onAdd,
  onDelete,
}: {
  title: string;
  empty: string;
  items: Array<{ id: string; primary: string; secondary: string | null }>;
  canEdit: boolean;
  canDelete: boolean;
  addPlaceholder: string;
  addSecondaryPlaceholder?: string;
  onAdd: (primary: string, secondary: string) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
}) {
  const [primary, setPrimary] = useState('');
  const [secondary, setSecondary] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const add = async () => {
    if (!primary.trim()) return;
    try {
      setBusy(true);
      setErr(null);
      await onAdd(primary.trim(), secondary.trim());
      setPrimary('');
      setSecondary('');
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ marginTop: 16 }}>
      <FieldLabel>{title}</FieldLabel>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
          marginTop: 6,
        }}
      >
        {items.length === 0 && (
          <div
            style={{
              fontSize: 'var(--fs-base)',
              color: 'var(--text-muted)',
              fontStyle: 'italic',
            }}
          >
            {empty}
          </div>
        )}
        {items.map((it) => (
          <div
            key={it.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '7px 10px',
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)',
              background: 'var(--bg-card)',
            }}
          >
            <span style={{ fontSize: 'var(--fs-base)', fontWeight: 600 }}>
              {it.primary}
            </span>
            {it.secondary && (
              <span
                style={{
                  fontSize: 'var(--fs-base)',
                  color: 'var(--text-secondary)',
                }}
              >
                {it.secondary}
              </span>
            )}
            {canDelete && (
              <button
                className="btn btn-outline btn-sm"
                style={{ marginLeft: 'auto', color: '#B22B2B' }}
                disabled={busy}
                onClick={async () => {
                  try {
                    setBusy(true);
                    await onDelete(it.id);
                  } catch (e) {
                    setErr(e instanceof Error ? e.message : String(e));
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                Remove
              </button>
            )}
          </div>
        ))}
      </div>

      {err && (
        <div className="banner banner-error" style={{ marginTop: 6 }}>
          {err}
        </div>
      )}

      {canEdit && (
        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
          <input
            className="input"
            placeholder={addPlaceholder}
            value={primary}
            onChange={(e) => setPrimary(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !addSecondaryPlaceholder) void add();
            }}
          />
          {addSecondaryPlaceholder && (
            <input
              className="input"
              placeholder={addSecondaryPlaceholder}
              value={secondary}
              onChange={(e) => setSecondary(e.target.value)}
            />
          )}
          <button
            className="btn btn-outline btn-sm"
            disabled={busy || !primary.trim()}
            onClick={add}
          >
            Add
          </button>
        </div>
      )}
    </div>
  );
}

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <span
      style={{
        fontSize: 'var(--fs-xs)',
        fontWeight: 600,
        color: 'var(--text-muted)',
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
      }}
    >
      {children}
    </span>
  );
}

function CheckField({
  label,
  sub,
  value,
  disabled,
  onChange,
}: {
  label: string;
  sub?: string;
  value: boolean;
  disabled?: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label
      style={{
        display: 'flex',
        gap: 8,
        alignItems: 'flex-start',
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.7 : 1,
      }}
    >
      <input
        type="checkbox"
        checked={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        style={{ marginTop: 2 }}
      />
      <span>
        <span style={{ fontSize: 'var(--fs-base)', fontWeight: 600 }}>
          {label}
        </span>
        {sub && (
          <span
            style={{
              display: 'block',
              fontSize: 'var(--fs-sm)',
              color: 'var(--text-muted)',
            }}
          >
            {sub}
          </span>
        )}
      </span>
    </label>
  );
}
